/**
 * A small in-memory cache for GET requests, so moving between pages doesn't
 * re-ask the server for what it just sent.
 *
 * - Callers asking for the same thing at the same time share one request.
 * - An answer is reused outright for REUSE_MS (bursts: several panels on one
 *   page loading the same list).
 * - The last good answer is kept for KEEP_MS so a page can show it at once
 *   (peekCache) while it refreshes in the background.
 * - Any write (POST/PUT/PATCH/DELETE) clears the lot — after a save nothing
 *   stale is ever shown. Failures are never cached.
 * - Every caller gets its own copy, so one screen changing its data can't
 *   change another's.
 */
export const REUSE_MS = 2000;
export const KEEP_MS = 5 * 60 * 1000;

const inflight = new Map();   // key -> Promise
const answers = new Map();    // key -> { value, at }

const copy = (v) => {
  if (v == null || typeof v !== 'object') return v;
  try { return typeof structuredClone === 'function' ? structuredClone(v) : JSON.parse(JSON.stringify(v)); } catch { return v; }
};

/** Fetch `key` with `fetcher`, sharing in-flight requests and reusing a just-finished answer. */
export function sharedGet(key, fetcher) {
  const hit = answers.get(key);
  if (hit && Date.now() - hit.at < REUSE_MS) return Promise.resolve(copy(hit.value));
  if (!inflight.has(key)) {
    const p = Promise.resolve()
      .then(fetcher)
      .then((value) => { answers.set(key, { value, at: Date.now() }); return value; })
      .finally(() => { inflight.delete(key); });
    inflight.set(key, p);
  }
  return inflight.get(key).then(copy);
}

/** The last good answer for `key` if it's recent enough to show while refreshing, else undefined. */
export function peekCache(key) {
  const hit = answers.get(key);
  if (!hit || Date.now() - hit.at > KEEP_MS) return undefined;
  return copy(hit.value);
}

/** Forget everything (after any write, and on sign-out). */
export function clearRequestCache() {
  inflight.clear();
  answers.clear();
}

/* Writes that don't go through apiFetch (file uploads over XHR, photo, floor-plan and
   PDF-template saves using fetch directly) must clear the cache too. Watch every
   request the page sends to /api/: anything but a GET clears it. Installed once. */
const isApiWrite = (url, method) => /\/api\//.test(String(url || '')) && String(method || 'GET').toUpperCase() !== 'GET';
if (typeof window !== 'undefined' && !window.__cwCacheWatch) {
  window.__cwCacheWatch = true;
  const origFetch = window.fetch && window.fetch.bind(window);
  if (origFetch) {
    window.fetch = (input, init) => {
      const url = typeof input === 'string' ? input : (input && input.url);
      const method = (init && init.method) || (input && input.method) || 'GET';
      if (isApiWrite(url, method)) clearRequestCache();
      return origFetch(input, init);
    };
  }
  if (typeof XMLHttpRequest !== 'undefined') {
    const origOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function open(method, url, ...rest) {
      if (isApiWrite(url, method)) clearRequestCache();
      return origOpen.call(this, method, url, ...rest);
    };
  }
}
